import type {
  ChatSocketItemDTO,
  ChatSocketLastMessagePreviewDTO,
  ChatSocketMessageDTO,
  ChatSocketMessageReplyPreviewDTO,
  ChatSocketPinnedMessageDTO,
  ChatSocketUserDTO,
} from '../realtime/chatSocketTypes';
import type {
  ChatLastMessagePreviewDTO,
  ChatListItemDTO,
  ChatMessageDTO,
  ChatPinnedMessageDTO,
  ChatReplyPreviewDTO,
  ChatUserDTO,
} from './dto';

function mapSocketUser(dto: ChatSocketUserDTO): ChatUserDTO {
  return {
    id: dto.id,
    firstName: dto.firstName,
    lastName: dto.lastName,
    username: dto.username,
    accountType: dto.accountType,
    avatar: dto.avatar,
  };
}

function mapSocketUserWithBadge(
  dto: ChatSocketLastMessagePreviewDTO['sender'],
): ChatUserDTO | null {
  if (!dto) {
    return null;
  }

  return {
    ...mapSocketUser(dto),
    badge: dto.badge,
  };
}

function mapSocketLastMessage(
  dto: ChatSocketLastMessagePreviewDTO | null,
): ChatLastMessagePreviewDTO | null {
  if (!dto) {
    return null;
  }

  return {
    id: dto.id,
    type: dto.type,
    text: dto.text,
    sender: mapSocketUserWithBadge(dto.sender),
    createdAt: dto.createdAt,
  };
}

function mapSocketReplyPreview(
  dto: ChatSocketMessageReplyPreviewDTO | null,
): ChatReplyPreviewDTO | null {
  if (!dto) {
    return null;
  }

  return {
    id: dto.id,
    type: dto.type,
    text: dto.text,
    attachments: dto.attachments,
    sender: dto.sender ? mapSocketUser(dto.sender) : null,
  };
}

export function mapSocketItemToListItem(dto: ChatSocketItemDTO): ChatListItemDTO {
  return {
    id: dto.id,
    type: dto.type,
    status: dto.status,
    avatar: dto.avatar,
    title: dto.title,
    origin: dto.origin,
    lastMessage: mapSocketLastMessage(dto.lastMessage),
    unreadCount: dto.unreadCount,
    lastMessageAt: dto.lastMessageAt,
    canLeave: dto.canLeave,
    mutedUntil: dto.mutedUntil,
    participant: dto.participant
      ? {
          userId: dto.participant.userId,
          isOnline: dto.participant.isOnline,
          lastSeenAt: dto.participant.lastSeenAt,
        }
      : null,
  };
}

export function mapSocketMessageToMessageDto(dto: ChatSocketMessageDTO): ChatMessageDTO {
  return {
    id: dto.id,
    type: dto.type,
    text: dto.text,
    attachments: dto.attachments,
    replyTo: mapSocketReplyPreview(dto.replyTo),
    sender: dto.sender ? mapSocketUser(dto.sender) : null,
    createdAt: dto.createdAt,
    readByMe: dto.readByMe,
    readByUserIds: dto.readByUserIds,
  };
}

export function mapSocketPinnedToPinnedDto(dto: ChatSocketPinnedMessageDTO): ChatPinnedMessageDTO {
  return {
    id: dto.id,
    type: dto.type,
    text: dto.text,
    sender: dto.sender ? mapSocketUser(dto.sender) : null,
    createdAt: dto.createdAt,
  };
}
